import { z } from "zod";
import { EQUIPMENT_TIERS, equipmentTierFactor, type EquipmentTier } from "./equipment-tiers.js";

export const SHIP_SYSTEM_IDS = ["reactor", "shield", "engine"] as const;
export type ShipSystemId = (typeof SHIP_SYSTEM_IDS)[number];
export const shipSystemIdSchema = z.enum(SHIP_SYSTEM_IDS);
const bonus = z.number().finite().nonnegative();
export const systemEffectSchema = z.strictObject({
  maxEnergy: bonus, energyPerSecond: bonus, maxShield: bonus, shieldPerSecond: bonus,
  speedFactor: bonus, turnRateFactor: bonus,
}).readonly();
export type SystemEffect = z.infer<typeof systemEffectSchema>;
const effects = z.tuple([systemEffectSchema, systemEffectSchema, systemEffectSchema]).readonly();
export const systemEffectsSchema = z.record(shipSystemIdSchema, effects).readonly();
export type SystemEffects = z.infer<typeof systemEffectsSchema>;
const base: Record<ShipSystemId, SystemEffect> = {
  reactor: { maxEnergy: 25, energyPerSecond: 4, maxShield: 0, shieldPerSecond: 0, speedFactor: 0, turnRateFactor: 0 },
  shield: { maxEnergy: 0, energyPerSecond: 0, maxShield: 80, shieldPerSecond: 5, speedFactor: 0, turnRateFactor: 0 },
  engine: { maxEnergy: 0, energyPerSecond: 0, maxShield: 0, shieldPerSecond: 0, speedFactor: 0.12, turnRateFactor: 0.06 },
};
export const SHIP_SYSTEMS = {
  reactor: { name: "Реактор", description: "Постоянно увеличивает запас и восстановление энергии. Занимает одну общую ячейку.", recipeComponents: 300 },
  shield: { name: "Генератор щита", description: "Постоянно добавляет щит и его восстановление. Вместо него можно поставить двигатель, реактор или активный модуль.", recipeComponents: 225 },
  engine: { name: "Двигатель", description: "Постоянно повышает обычную скорость и скорость разворота. Занимает одну общую ячейку.", recipeComponents: 225 },
} satisfies Record<ShipSystemId, { name: string; description: string; recipeComponents: number }>;

/** Capture alongside the combat rules. Repeated installed copies add their frozen bonuses. */
export function captureSystemEffects(): SystemEffects {
  return systemEffectsSchema.parse(Object.fromEntries(SHIP_SYSTEM_IDS.map(id => [id,
    EQUIPMENT_TIERS.map(tier => {
      const factor = equipmentTierFactor(tier), effect = base[id];
      return { maxEnergy: effect.maxEnergy * factor, energyPerSecond: effect.energyPerSecond * factor,
        maxShield: effect.maxShield * factor, shieldPerSecond: effect.shieldPerSecond * factor,
        speedFactor: effect.speedFactor * factor, turnRateFactor: effect.turnRateFactor * factor };
    }),
  ])));
}
export function systemParameters(id: ShipSystemId, tier: EquipmentTier): [
  { label: string; value: number; unit: string }, { label: string; value: number; unit: string },
] {
  const effect = captureSystemEffects()[id][tier];
  if (id === "reactor") return [{ label: "Дополнительная энергия", value: effect.maxEnergy, unit: "" },
    { label: "Восстановление энергии", value: effect.energyPerSecond, unit: "/с" }];
  if (id === "shield") return [{ label: "Запас щита", value: effect.maxShield, unit: "" },
    { label: "Восстановление щита", value: effect.shieldPerSecond, unit: "/с" }];
  return [{ label: "Обычная скорость", value: effect.speedFactor * 100, unit: "%" },
    { label: "Скорость разворота", value: effect.turnRateFactor * 100, unit: "%" }];
}
